import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { RRule, Weekday } from 'rrule';
import { sanitizeRecurrence } from './recurrenceFields';
import {
  RecurrenceFrequency,
  RecurrenceOccurrence,
  RecurrenceRule,
  RecurrenceValues,
} from './types';

dayjs.extend(utc);
dayjs.extend(timezone);

const OCCURRENCE_COUNT = 99;
const OCCURRENCE_FORMAT = 'ddd MMM DD YYYY HH:mm:ss';

const FREQ_BY_VALUE: Record<RecurrenceFrequency, number> = {
  daily: RRule.DAILY,
  weekly: RRule.WEEKLY,
  monthly: RRule.MONTHLY,
  yearly: RRule.YEARLY,
};

const WEEKDAY_BY_CODE: Record<string, Weekday> = {
  MO: RRule.MO,
  TU: RRule.TU,
  WE: RRule.WE,
  TH: RRule.TH,
  FR: RRule.FR,
  SA: RRule.SA,
  SU: RRule.SU,
};

const emptyRule = (): RecurrenceRule => ({ ruleText: '', occurrences: [] });

const pad = (value: number): string => String(value).padStart(2, '0');

export const toHourList = (hour: RecurrenceValues['hour']): number[] => {
  const list = Array.isArray(hour) ? hour : hour === null || hour === undefined ? [] : [hour];
  return list.filter((value) => value !== null && value !== undefined);
};

const withHour = (text: string, hour: number): string =>
  text.includes(' at ') ? text : `${text} at ${pad(hour)}:00`;

const utcDay = (date: Date): string =>
  `${date.getUTCFullYear()}-${pad(date.getUTCMonth() + 1)}-${pad(date.getUTCDate())}`;

const toOccurrences = (dates: Date[], hour: number, timeZone: string): RecurrenceOccurrence[] => {
  const byLabel = new Map<string, RecurrenceOccurrence>();
  dates.forEach((date) => {
    const local = dayjs.tz(`${utcDay(date)} ${pad(hour)}:00:00`, timeZone);
    const label = `${local.format(OCCURRENCE_FORMAT)} (${timeZone})`;
    byLabel.set(label, { date: label, key: label, time: local.valueOf() });
  });
  return Array.from(byLabel.values());
};

export const buildRecurrenceRule = (input: RecurrenceValues): RecurrenceRule => {
  // RRule.YEARLY is 0, so this must test for undefined rather than truthiness
  const freq = input?.freq ? FREQ_BY_VALUE[input.freq] : undefined;
  if (freq === undefined) return emptyRule();

  const values = sanitizeRecurrence(input);
  const timeZone = values.tzid || Intl.DateTimeFormat().resolvedOptions().timeZone;
  const hours = toHourList(values.hour);
  const today = dayjs().tz(timeZone);

  // no tzid: toOccurrences already renders in timeZone, doing both shifts dates a whole day
  const rule = new RRule({
    freq,
    dtstart: new Date(Date.UTC(today.year(), today.month(), today.date(), 0, 0, 0)),
    count: OCCURRENCE_COUNT,
    interval: 1,
    byhour: hours.length ? hours : undefined,
    byweekday: values.weekday?.length
      ? values.weekday.map((code) => WEEKDAY_BY_CODE[code]).filter(Boolean)
      : undefined,
    bymonth: values.month?.length ? values.month : undefined,
    bysetpos: values.setpos || undefined,
    byweekno: values.week_number || undefined,
    bymonthday: values.month_day || undefined,
    byyearday: values.year_day || undefined,
  });

  const hour = hours.length ? hours[0] : null;
  const text = rule.toText().replace(` for ${OCCURRENCE_COUNT} times`, '');
  const now = Date.now();

  return {
    ruleText: hour === null ? text : withHour(text, hour),
    occurrences: toOccurrences(rule.all(), hour ?? 0, timeZone).filter(
      (occurrence) => occurrence.time >= now
    ),
  };
};
